import React, { useState } from "react";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";
import { useTranslation } from "react-i18next";
import instance from "../../Interceptor/api_instance";
import { TrashIconCont } from "../../Media/Icons";
import {
  getLocalDateTime,
  toDatetimeLocal,
} from "../../Helper/FormatDatetimes";
import { useFetch } from "../../Hooks/useFetch";
import { useApiMutation } from "../../Hooks/useApiMutation";
import NewTruckSearchSelect from "../../Components/Inputs/NewTruckSearch";
import { SuccessMessage } from "../../Components/LoadingAnimations/APIResponses";

function toUtcIso(localDateTime) {
  const d = new Date(localDateTime);
  return d.toISOString();
}

export const ReservationConfirm = ({ trailer, refetch, close }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    truckOption: null,
    reservationDate: null,
    carrierName: "",
  });

  const { mutate, isLoading, error } = useApiMutation(async (data) => {
    await instance.post(`assets/reservations/`, {
      trailer: trailer.id,
      truck_asset: data.truckOption ? data.truckOption.value : null,
      due_datetime: toUtcIso(data.reservationDate),
    });
    refetch();
    close();
  });

  return (
    <div className="page-mask page-mask-animation">
      <div className="blank-card active-confirm">
        <>
          <h4 className="">{`${t("graphics-cont.reservations.reserve")} ${
            trailer.trailer_number
          }`}</h4>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutate(formData);
            }}
          >
            {/* Form group: select carrier */}
            <div className="form-group">
              <label>
                {t("yard-tables.table-columns.carrier")}{" "}
                {`(${t("general.optional")})`}
              </label>
              <div className="input-cont">
                <select
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      carrierName: e.target.value,
                      truckOption: null,
                    }));
                  }}
                >
                  <option value="">
                    {t("forms.type.vehicle.placeholders.select-carrier")}
                  </option>
                  <option value="jfg">JFG</option>
                  <option value="ati">ATI</option>
                </select>
              </div>
            </div>

            {/* Form group: truck number */}
            {formData.carrierName !== "" && (
              <div className="form-group">
                <label className="input-label required">
                  {t("forms.type.vehicle.input-headers.truck-number")}{" "}
                </label>
                {formData.carrierName !== "" && (
                  <NewTruckSearchSelect
                    key={formData.carrierName || "no-carrier"}
                    selectedTruck={formData.truckOption}
                    onSelect={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        truckOption: option,
                      }))
                    }
                    enableAll={true}
                    carrier={formData.carrierName}
                  />
                )}
              </div>
            )}

            {/* Form group: date time */}
            <div className="form-group">
              <label className="input-label required">
                {t("yard-tables.table-columns.datetime")}
              </label>
              <div className="input-cont">
                <input
                  type="datetime-local"
                  value={formData.reservationDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      reservationDate: e.target.value,
                    }))
                  }
                  min={getLocalDateTime()}
                  className="text-input"
                  required
                />
              </div>
            </div>
            {error && <p className="error">{t("forms.responses.failure")}</p>}
            {isLoading ? (
              <Spinner />
            ) : (
              <div className="d-flex gap-2 justify-content-center">
                <button className="profile-pic-btn" type="submit">
                  {t("buttons.submit")}
                </button>
                <button onClick={close} className="cancel-btn">
                  {t("buttons.close")}
                </button>
              </div>
            )}
          </form>
        </>
      </div>
    </div>
  );
};

export const UnreservePrompt = ({ reservationId, close, onSuccess }) => {
  const { t } = useTranslation();

  const { mutate, isLoading, error } = useApiMutation(async () => {
    await instance.delete(`assets/reservations/${reservationId}/`);
    onSuccess();
  });
  return (
    <div className="page-mask-2 page-mask-animation">
      <div className="blank-card active-confirm">
        {error && <p className="error">{t("forms.responses.failure")}</p>}
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <h4 style={{ textAlign: "center" }}>
              {t("graphics-cont.reservations.unreserve-prompt")}
            </h4>
            <div className="d-flex gap-2 justify-content-center">
              <button onClick={mutate} className="profile-pic-btn">
                {t("general.yes")}
              </button>
              <button onClick={close} className="profile-pic-btn">
                No
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const EditReservation = ({ reservation_id, close, refetch }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    carrier: null,
    truckOption: null,
    reservationDate: null,
  });
  const [unreserveState, setUnreserveState] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { data, isLoading, error } = useFetch(async () => {
    const response = await instance.get(
      `assets/reservations/${reservation_id}`
    );
    const responseData = response.data;
    setFormData({
      carrier: responseData
        ? responseData.truck_asset
          ? responseData.truck_asset.company.toLowerCase()
          : null
        : null,
      truckOption: responseData
        ? responseData.truck_asset
          ? {
              value: responseData.truck_asset.id,
              label: responseData.truck_asset.unit,
            }
          : null
        : null,
      reservationDate: responseData
        ? toDatetimeLocal(responseData.due_datetime)
        : null,
    });
    return responseData;
  });

  const {
    mutate,
    isLoading: resLoading,
    error: resError,
  } = useApiMutation(async (updatedData) => {
    const response = await instance.patch(
      `assets/reservations/${reservation_id}/`,
      updatedData
    );
    if (response.status === 200) setShowSuccess(true);
    return response.data;
  });

  return (
    <>
      {unreserveState && (
        <UnreservePrompt
          reservationId={reservation_id}
          close={() => setUnreserveState(false)}
          onSuccess={() => {
            setUnreserveState(false);
            refetch();
            close();
          }}
        />
      )}
      <div className="page-mask page-mask-animation">
        <div className="questions-cont">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutate({
                truck_asset: formData.truckOption
                  ? formData.truckOption.value
                  : null,
                due_datetime: toUtcIso(formData.reservationDate),
              });
            }}
          >
            {isLoading ? (
              <Spinner />
            ) : data ? (
              <>
                <div className="full-cont">
                  <h4 className="">{`${t(
                    "graphics-cont.reservations.reserve"
                  )} ${data.trailer.trailer_number}`}</h4>
                  <TrashIconCont onClick={() => setUnreserveState(true)} />
                </div>
                <div className="form-block">
                  <label className="input-label">
                    {t("forms.type.vehicle.labels.carrierName")}{" "}
                    {`(${t("general.optional")})`}
                  </label>
                  <div className="input-cont">
                    <select
                      className="text-input"
                      value={formData.carrier}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          carrier: e.target.value,
                          truckOption: null,
                        }));
                      }}
                    >
                      <option value="">
                        {t("forms.type.vehicle.select.carrierName.default")}
                      </option>
                      <option value="jfg">JFG</option>
                      <option value="ati">ATI</option>
                      <option value="mfs">MFS</option>
                    </select>
                  </div>
                </div>
                {formData.carrier && (
                  <div className="form-block">
                    <label className="input-label required">
                      {t("yard-tables.tabs.truck")}
                    </label>
                    <div className="input-cont">
                      <NewTruckSearchSelect
                        onSelect={(option) =>
                          setFormData((prev) => ({
                            ...prev,
                            truckOption: option,
                          }))
                        }
                        carrier={formData.carrier}
                        selectedTruck={formData.truckOption}
                        enableAll={true}
                      />
                    </div>
                  </div>
                )}
                {/* Form group: date time */}
                <div className="form-block">
                  <label className="input-label required">
                    {t("yard-tables.table-columns.datetime")}
                  </label>
                  <div className="input-cont">
                    <input
                      type="datetime-local"
                      value={formData.reservationDate}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          reservationDate: e.target.value,
                        }))
                      }
                      min={getLocalDateTime()}
                      className="text-input"
                      required
                    />
                  </div>
                </div>
              </>
            ) : error ? (
              <p className="error">{t("forms.responses.failure")}</p>
            ) : null}
            <div className="full-cont">
              {resLoading ? (
                <Spinner />
              ) : (
                <>
                  {resError && (
                    <p className="error">{t("forms.responses.failure")}</p>
                  )}
                  {showSuccess && (
                    <SuccessMessage
                      message={t("forms.responses.success")}
                      onClose={() => setShowSuccess(false)}
                    />
                  )}
                  <button className="change-pwd-btn all-btn" type="submit">
                    {t("buttons.submit")}
                  </button>
                  <button className="cancel-btn all-btn" onClick={close}>
                    {t("buttons.close")}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
